import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, getToken } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * App Lock — an on-device gate that sits over an already-authenticated
 * session. Users stay logged in for weeks; App Lock ensures a stolen or
 * shared phone still needs a PIN (or biometric) to reveal workspace data.
 *
 * State machine (client-side only — the server is the source of truth for
 * the PIN hash and rate-limits):
 *   - locked === true   → LockScreen overlays the app, blocks input
 *   - locked === false  → App is usable
 *   - unlocked at boot only when app_lock_enabled === false
 *
 * Lock triggers:
 *   - Fresh app boot when config.enabled
 *   - Tab becomes hidden → record timestamp
 *   - Tab becomes visible AND (now - hidden_at) >= timeout_seconds → lock
 *   - Timeout === 0 → lock immediately on any hide/reopen
 */

const AppLockCtx = createContext(null);
const LAST_ACTIVE_KEY = "vu_lock_last_active";
// Used across cold boots (PWA close/reopen).
const SESSION_UNLOCK_KEY = "vu_lock_unlocked_at";

export function AppLockProvider({ children }) {
    const { user } = useAuth();
    const [config, setConfig] = useState(null);
    const [locked, setLocked] = useState(true);   // pessimistic default
    const [hiddenSince, setHiddenSince] = useState(null);
    const timeoutRef = useRef(300); // seconds; default 5m

    // Load config whenever we have a logged-in user.
    const loadConfig = useCallback(async () => {
        if (!user?.user_id || !getToken()) {
            setConfig(null);
            setLocked(false);
            return;
        }
        try {
            const { data } = await api.get("/security/app-lock");
            setConfig(data);
            timeoutRef.current = Number(data.timeout_seconds ?? 300);
            if (!data.enabled) {
                setLocked(false);
                return;
            }
            // Decide whether to lock on boot: if session was previously
            // unlocked within the timeout window, keep it unlocked.
            const prevUnlock = Number(sessionStorage.getItem(SESSION_UNLOCK_KEY) || 0);
            const lastActive = Number(localStorage.getItem(LAST_ACTIVE_KEY) || 0);
            const elapsed = (Date.now() - Math.max(prevUnlock, lastActive)) / 1000;
            const shouldLock =
                data.timeout_seconds === 0 ||
                !prevUnlock ||
                elapsed >= data.timeout_seconds;
            setLocked(shouldLock);
        } catch {
            // Non-fatal: keep the app usable if the config can't be fetched
            // (offline PWA startup, transient 5xx). Assume "not locked" so
            // we never accidentally block a user out of their own data.
            setConfig(null);
            setLocked(false);
        }
    }, [user?.user_id]);

    useEffect(() => { loadConfig(); }, [loadConfig]);

    // Track visibility → schedule lock check on return.
    useEffect(() => {
        if (!config?.enabled) return undefined;
        const onVisibility = () => {
            if (document.visibilityState === "hidden") {
                const ts = Date.now();
                setHiddenSince(ts);
                localStorage.setItem(LAST_ACTIVE_KEY, String(ts));
            } else if (document.visibilityState === "visible") {
                const away = hiddenSince ? (Date.now() - hiddenSince) / 1000 : Infinity;
                if (timeoutRef.current === 0 || away >= timeoutRef.current) {
                    setLocked(true);
                }
                setHiddenSince(null);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, [config?.enabled, hiddenSince]);

    // Track activity so the "last active" timestamp is accurate for
    // cold-boot decisions.
    useEffect(() => {
        if (!config?.enabled || locked) return undefined;
        const bump = () => localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
        const events = ["click", "keydown", "touchstart", "mousemove"];
        events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
        bump();
        return () => events.forEach((e) => window.removeEventListener(e, bump));
    }, [config?.enabled, locked]);

    const markUnlocked = useCallback(() => {
        const now = Date.now();
        sessionStorage.setItem(SESSION_UNLOCK_KEY, String(now));
        localStorage.setItem(LAST_ACTIVE_KEY, String(now));
        setLocked(false);
    }, []);

    const requireLock = useCallback(() => setLocked(true), []);

    const verifyPin = useCallback(async (pin) => {
        const { data } = await api.post("/security/app-lock/verify", { pin });
        markUnlocked();
        return data;
    }, [markUnlocked]);

    const biometricUnlock = useCallback(async () => {
        const { data: opts } = await api.post("/security/webauthn/auth-begin", {});
        const publicKey = webauthnDecodeAuthOptions(opts);
        const assertion = await navigator.credentials.get({ publicKey });
        const credential = webauthnEncodeAssertion(assertion);
        const { data } = await api.post("/security/webauthn/auth-complete", { credential });
        markUnlocked();
        return data;
    }, [markUnlocked]);

    const registerBiometric = useCallback(async () => {
        const { data: opts } = await api.post("/security/webauthn/register-begin", {});
        const publicKey = webauthnDecodeRegOptions(opts);
        const cred = await navigator.credentials.create({ publicKey });
        const credential = webauthnEncodeAttestation(cred);
        const { data } = await api.post("/security/webauthn/register-complete", { credential });
        setConfig(data);
        return data;
    }, []);

    return (
        <AppLockCtx.Provider value={{
            config, locked, verifyPin, requireLock, markUnlocked,
            biometricUnlock, registerBiometric, reload: loadConfig, setConfig,
        }}>
            {children}
        </AppLockCtx.Provider>
    );
}

export const useAppLock = () => useContext(AppLockCtx);

// --- WebAuthn helpers ------------------------------------------------

function b64urlToBytes(s) {
    const pad = "=".repeat((4 - (s.length % 4)) % 4);
    const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
}

function bytesToB64url(buf) {
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function webauthnDecodeRegOptions(opts) {
    return {
        ...opts,
        challenge: b64urlToBytes(opts.challenge),
        user: { ...opts.user, id: b64urlToBytes(opts.user.id) },
        excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
            ...c, id: b64urlToBytes(c.id),
        })),
    };
}

function webauthnDecodeAuthOptions(opts) {
    return {
        ...opts,
        challenge: b64urlToBytes(opts.challenge),
        allowCredentials: (opts.allowCredentials || []).map((c) => ({
            ...c, id: b64urlToBytes(c.id),
        })),
    };
}

function webauthnEncodeAttestation(cred) {
    return {
        id: cred.id,
        rawId: bytesToB64url(cred.rawId),
        type: cred.type,
        response: {
            attestationObject: bytesToB64url(cred.response.attestationObject),
            clientDataJSON: bytesToB64url(cred.response.clientDataJSON),
        },
    };
}

function webauthnEncodeAssertion(cred) {
    return {
        id: cred.id,
        rawId: bytesToB64url(cred.rawId),
        type: cred.type,
        response: {
            authenticatorData: bytesToB64url(cred.response.authenticatorData),
            clientDataJSON: bytesToB64url(cred.response.clientDataJSON),
            signature: bytesToB64url(cred.response.signature),
            userHandle: cred.response.userHandle ? bytesToB64url(cred.response.userHandle) : null,
        },
    };
}

export function biometricAvailable() {
    return typeof window !== "undefined" &&
           "credentials" in navigator &&
           typeof PublicKeyCredential !== "undefined";
}
